import React from 'react';
import {
  parseSenderDisplay,
  getSenderInitial,
  getAvatarColor,
  getPreviewText,
  formatMailDate,
} from '../utils/mailViewHelpers';

const StarIcon = ({ filled }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    {filled ? (
      <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
    ) : (
      <path d="M22 9.24l-7.19-.62L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.63-7.03L22 9.24zM12 15.4l-3.76 2.27 1-4.28-3.32-2.88 4.38-.38L12 6.1l1.71 4.04 4.38.38-3.32 2.88 1 4.28L12 15.4z" />
    )}
  </svg>
);

/**
 * Shared Gmail-style mail row used by every folder view so the list layout
 * matches the Inbox exactly.
 */
const MailListRow = ({
  mail,
  mailKey,
  isChecked = false,
  isOpen = false,
  isUnread = false,
  isStarred = false,
  onOpen,
  onToggleCheck,
  onToggleStar,
  senderText,
  avatarSeed,
  preview,
  dateText,
  badge,
  actions = [],
  currentUserEmail,
}) => {
  const avatarColors = getAvatarColor(avatarSeed ?? mail.from);
  const previewText = preview !== undefined ? preview : getPreviewText(mail);
  const displayDate = dateText !== undefined ? dateText : formatMailDate(mail.createdAt || mail.date);
  const showStar = typeof onToggleStar === 'function';
  const hasAttachments = Boolean(mail.attachments && mail.attachments.length > 0);
  const displaySender =
    senderText !== undefined
      ? senderText
      : parseSenderDisplay(mail.from, currentUserEmail);

  const rowClasses = [
    'mail-item',
    'gmail-split-row',
    isUnread ? 'unread' : 'read',
    isOpen ? 'selected' : '',
    isChecked ? 'mail-selected mobile-row-selected' : '',
    hasAttachments ? 'has-attachments' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      role="button"
      tabIndex={0}
      className={rowClasses}
      onClick={() => onOpen?.(mail)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen?.(mail);
        }
      }}
    >
      <span className="gmail-row-drag-handle" aria-hidden="true">
        <svg width="12" height="20" viewBox="0 0 12 20" fill="currentColor">
          <circle cx="3" cy="4" r="1.5" />
          <circle cx="9" cy="4" r="1.5" />
          <circle cx="3" cy="10" r="1.5" />
          <circle cx="9" cy="10" r="1.5" />
          <circle cx="3" cy="16" r="1.5" />
          <circle cx="9" cy="16" r="1.5" />
        </svg>
      </span>

      <input
        type="checkbox"
        className="mail-checkbox gmail-col-check"
        checked={isChecked}
        onChange={() => onToggleCheck?.(mailKey)}
        onClick={(e) => e.stopPropagation()}
        aria-label="Select mail"
      />

      {showStar && (
        <button
          type="button"
          className={`mail-star-btn gmail-row-star gmail-row-star-left ${isStarred ? 'starred' : ''}`}
          onClick={(e) => onToggleStar(e, mail)}
          title={isStarred ? 'Unstar' : 'Star'}
        >
          <StarIcon filled={isStarred} />
        </button>
      )}

      <span className={`mail-read-dot ${isUnread ? 'visible' : ''}`} aria-hidden="true" />

      <div
        className={`mail-sender-avatar ${isChecked ? 'avatar-selected' : ''}`}
        style={!isChecked ? { backgroundColor: avatarColors.bg, color: avatarColors.color } : undefined}
        aria-hidden="true"
      >
        {isChecked ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
          </svg>
        ) : (
          getSenderInitial(avatarSeed ?? mail.from)
        )}
      </div>

      <div className="gmail-row-body">
        <div className={`gmail-row-primary${badge ? ' has-folder-badge' : ''}`}>
          <span className="mail-sender-hover-wrap">
            <span className="mail-sender">
              {displaySender}
            </span>
          </span>
          {badge ? (
            <span className="fv-row-badge" data-folder={badge}>
              {badge}
            </span>
          ) : null}
          <div className="gmail-row-main-line">
            <div className="gmail-row-subject">
              <span className="gmail-row-subject-text">{mail.subject || '(no subject)'}</span>
            </div>
            {previewText ? <span className="gmail-row-snippet">{previewText}</span> : null}
          </div>
        </div>
      </div>

      <div className="gmail-row-aside">
        <span className="mail-date">{displayDate}</span>
        {actions.length > 0 && (
          <div className="gmail-row-hover-actions">
            {actions.map((action) => (
              <button
                type="button"
                key={action.key || action.title}
                className="mail-hover-btn"
                title={action.title}
                aria-label={action.title}
                onClick={(e) => {
                  e.stopPropagation();
                  action.onClick(e, mail);
                }}
              >
                {action.icon}
              </button>
            ))}
          </div>
        )}
        {showStar && (
          <button
            type="button"
            className={`mail-star-btn gmail-row-star gmail-row-star-aside ${isStarred ? 'starred' : ''}`}
            onClick={(e) => onToggleStar(e, mail)}
            title={isStarred ? 'Unstar' : 'Star'}
          >
            <StarIcon filled={isStarred} />
          </button>
        )}
      </div>
    </div>
  );
};

export default MailListRow;
